import { NgOptimizedImage } from '@angular/common';
import { Component, DestroyRef, effect, inject, OnInit, signal, untracked } from '@angular/core';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import { BannersPublicosService } from '../../services/banners-publicos.service';
import { RedesSociaisComponent } from '../redes-sociais/redes-sociais.component';

const INTERVALO_AUTOPLAY_MS = 6000;
@Component({
  selector: 'app-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.css'],
  imports: [NgOptimizedImage, LucideModule, RedesSociaisComponent],
})
export class BannerComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly bannersPublicos = inject(BannersPublicosService);
  private intervaloId?: ReturnType<typeof setInterval>;

  /** Os banners que o órgão exibe hoje. Vazio enquanto carrega e se a API falhar. */
  readonly slides = this.bannersPublicos.slides;

  readonly indiceAtivo = signal(0);

  /** Os banners cuja arte não carregou: o slide fica só com o gradiente. */
  readonly imagensComErro = signal<ReadonlySet<number>>(new Set());

  constructor() {
    // Os slides chegam depois do primeiro desenho: só então o autoplay tem o que girar.
    effect(() => {
      const total = this.slides().length;

      untracked(() => {
        if (this.indiceAtivo() >= total) this.indiceAtivo.set(0);
        this.pararAutoplay();
        this.iniciarAutoplay();
      });
    });
  }

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.pararAutoplay());
  }

  proximo(): void {
    const total = this.slides().length;
    this.indiceAtivo.update((atual) => (atual + 1) % total);
  }

  anterior(): void {
    const total = this.slides().length;
    this.indiceAtivo.update((atual) => (atual - 1 + total) % total);
  }

  irPara(indice: number): void {
    this.indiceAtivo.set(indice);
  }

  registrarErroImagem(id: number): void {
    this.imagensComErro.update((atual) => new Set(atual).add(id));
  }

  pausarAutoplay(): void {
    this.pararAutoplay();
  }

  retomarAutoplay(): void {
    this.iniciarAutoplay();
  }

  private iniciarAutoplay(): void {
    if (this.slides().length <= 1) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    this.pararAutoplay();
    this.intervaloId = setInterval(() => this.proximo(), INTERVALO_AUTOPLAY_MS);
  }

  private pararAutoplay(): void {
    if (this.intervaloId !== undefined) {
      clearInterval(this.intervaloId);
      this.intervaloId = undefined;
    }
  }
}
